import React, { useState, useEffect } from "react";
import RefundPolicy from "./RefundPolicy";
import { useNavigate } from "react-router-dom";
import { motion } from 'framer-motion';
import Icon from 'components/AppIcon';

function WebinarHeader({ webinar, isAuthenticated, userRole }) {
  const navigate = useNavigate();
  
  return (
    <div className="bg-gradient-to-br from-green-900 via-emerald-900 to-green-800 text-white relative overflow-hidden">
      {/* Animated Background Pattern */}
      <div className="absolute inset-0 opacity-5">
        <div className="absolute inset-0" style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='%23a7f3d0' fill-opacity='0.2'%3E%3Cpath d='M30 30c0-16.569-13.431-30-30-30v30h30z'/%3E%3C/g%3E%3C/svg%3E")`,
          backgroundSize: '60px 60px'
        }}></div>
      </div>
      
      {/* Floating Animated Elements */}
      <motion.div 
        className="absolute top-10 left-10 w-72 h-72 bg-emerald-400/10 rounded-full blur-3xl"
        animate={{ 
          scale: [1, 1.2, 1],
          opacity: [0.1, 0.2, 0.1]
        }}
        transition={{ 
          duration: 8,
          repeat: Infinity,
          ease: "easeInOut"
        }}
      />
      <motion.div 
        className="absolute bottom-10 right-10 w-64 h-64 bg-green-300/10 rounded-full blur-3xl"
        animate={{ 
          scale: [1.2, 1, 1.2],
          opacity: [0.2, 0.1, 0.2]
        }}
        transition={{ 
          duration: 6,
          repeat: Infinity,
          ease: "easeInOut"
        }}
      />

      <div className="max-w-8xl mx-auto px-3 sm:px-4 lg:px-8 py-6 sm:py-8 relative z-10">
        <div className="flex items-center space-x-2 text-sm text-white/80 mb-3 sm:mb-4"></div>
        
        {/* Main Layout */}
        <div className="flex flex-col lg:flex-row gap-6 lg:gap-4">
          {/* Left Side: Content */}
          <div className="w-full lg:w-[75%] flex">
            <div className="w-full lg:w-[93.33%]">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
                className="mb-4 sm:mb-6"
              >
                <div className="flex flex-wrap gap-3 mb-1">
                  {webinar.isLiveNow ? (
                    <motion.span 
                      className="bg-gradient-to-r from-emerald-500 to-green-600 text-white px-3 py-2 rounded-full text-xs font-bold shadow-lg shadow-emerald-500/20"
                      animate={{ 
                        scale: [1, 1.05, 1],
                        boxShadow: [
                          "0 0 0 0 rgba(16, 185, 129, 0)",
                          "0 0 0 10px rgba(16, 185, 129, 0.3)",
                          "0 0 0 0 rgba(16, 185, 129, 0)"
                        ]
                      }}
                      transition={{ 
                        duration: 2,
                        repeat: Infinity,
                        ease: "easeInOut"
                      }}
                    >
                      <div className="w-2 h-2 bg-white rounded-full inline-block mr-2"></div>
                      LIVE NOW
                    </motion.span>
                  ) : webinar.isUpcoming ? (
                    <motion.span 
                      className="bg-gradient-to-r from-green-500 to-emerald-600 text-white px-3 py-2 rounded-full text-xs font-bold shadow-lg shadow-green-500/20"
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                    >
                      <Icon name="Calendar" size={12} className="inline mr-1" />
                      UPCOMING
                    </motion.span>
                  ) : webinar.isCompleted ? (
                    <motion.span 
                      className="bg-gradient-to-r from-green-400 to-emerald-500 text-white px-3 py-2 rounded-full text-xs font-bold shadow-lg shadow-green-400/20"
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                    >
                      <Icon name="Clock" size={12} className="inline mr-1" />
                      COMPLETED
                    </motion.span>
                  ) : (
                    <motion.span 
                      className="bg-gradient-to-r from-green-500 to-emerald-600 text-white px-3 py-2 rounded-full text-xs font-bold shadow-lg shadow-green-500/20"
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                    >
                      <Icon name="Video" size={12} className="inline mr-1" />
                      LIVE WEBINAR
                    </motion.span>
                  )}
                  
                  {webinar.hasFullAccess && (
                    <motion.span 
                      className="bg-gradient-to-r from-emerald-400 to-green-500 text-white px-3 py-2 rounded-full text-xs font-bold shadow-lg shadow-emerald-400/20"
                      initial={{ scale: 0 }}
                      animate={{ scale: 1 }}
                      transition={{ type: "spring", stiffness: 200 }}
                      whileHover={{ scale: 1.05 }}
                    >
                      <Icon name="CheckCircle" size={12} className="inline mr-1" />
                      ENROLLED
                    </motion.span>
                  )}
                </div>
                
                <motion.h1 
                  className="text-xl sm:text-2xl md:text-2xl lg:text-3xl font-bold mb-4 sm:mb-6 leading-tight"
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.6, delay: 0.1 }}
                >
                  {webinar.title}
                </motion.h1>
              </motion.div>

              {/* Schedule Section */}
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.2 }}
                whileHover={{ y: -5 }}
                className="bg-gradient-to-br from-green-800/40 to-emerald-900/40 backdrop-blur-sm rounded-xl sm:rounded-2xl p-4 sm:p-6 lg:p-8 mb-4 sm:mb-6 border border-green-300/20 shadow-2xl shadow-green-900/20"
              >
                <h3 className="text-sm sm:text-base lg:text-lg font-bold mb-4 sm:mb-6 text-white flex items-center">
                  <motion.div
                    animate={{ rotate: [0, 10, 0] }}
                    transition={{ duration: 2, repeat: Infinity }}
                  >
                    <Icon name="Calendar" size={16} className="mr-2 text-emerald-300" />
                  </motion.div>
                  Webinar Schedule
                </h3>
                
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
                  <motion.div 
                    className="flex items-start space-x-3 sm:space-x-4 bg-green-900/30 rounded-xl p-3 hover:bg-green-900/50 transition-all duration-300"
                    whileHover={{ scale: 1.02 }}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.4, delay: 0.3 }}
                  >
                    <div className="w-8 h-8 sm:w-10 sm:h-10 md:w-12 md:h-12 bg-gradient-to-br from-green-500 to-emerald-600 rounded-full flex items-center justify-center flex-shrink-0 border-2 border-green-300/50 shadow-lg shadow-green-500/20">
                      <Icon name="Calendar" size={12} className="text-green-100" />
                    </div>
                    <div className="min-w-0">
                      <div className="font-semibold text-sm sm:text-base text-emerald-100 mb-1">Date:</div>
                      <div className="text-green-100 text-xs sm:text-base">{webinar.date}</div>
                    </div>
                  </motion.div>
                  
                  <motion.div 
                    className="flex items-start space-x-3 sm:space-x-4 bg-green-900/30 rounded-xl p-3 hover:bg-green-900/50 transition-all duration-300"
                    whileHover={{ scale: 1.02 }}
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.4, delay: 0.35 }}
                  >
                    <div className="w-8 h-8 sm:w-10 sm:h-10 md:w-12 md:h-12 bg-gradient-to-br from-emerald-500 to-green-600 rounded-full flex items-center justify-center flex-shrink-0 border-2 border-emerald-300/50 shadow-lg shadow-emerald-500/20">
                      <Icon name="Clock" size={12} className="text-green-100" />
                    </div>
                    <div className="min-w-0">
                      <div className="font-semibold text-sm sm:text-base text-emerald-100 mb-1">Time:</div>
                      <div className="text-green-100 text-xs sm:text-base">{webinar.time}</div>
                    </div>
                  </motion.div>
                  
                  <motion.div 
                    className="flex items-start space-x-3 sm:space-x-4 bg-green-900/30 rounded-xl p-3 hover:bg-green-900/50 transition-all duration-300"
                    whileHover={{ scale: 1.02 }}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.4, delay: 0.4 }}
                  >
                    <div className="w-8 h-8 sm:w-10 sm:h-10 md:w-12 md:h-12 bg-gradient-to-br from-green-400 to-emerald-500 rounded-full flex items-center justify-center flex-shrink-0 border-2 border-green-200/50 shadow-lg shadow-green-400/20">
                      <Icon name="Timer" size={12} className="text-green-100" />
                    </div>
                    <div className="min-w-0">
                      <div className="font-semibold text-sm sm:text-base text-emerald-100 mb-1">Duration:</div>
                      <div className="text-green-100 text-xs sm:text-base">{webinar.duration}</div>
                    </div>
                  </motion.div>
                  
                  <motion.div 
                    className="flex items-start space-x-3 sm:space-x-4 bg-green-900/30 rounded-xl p-3 hover:bg-green-900/50 transition-all duration-300"
                    whileHover={{ scale: 1.02 }}
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.4, delay: 0.45 }}
                  >
                    <div className="w-8 h-8 sm:w-10 sm:h-10 md:w-12 md:h-12 bg-gradient-to-br from-emerald-400 to-green-500 rounded-full flex items-center justify-center flex-shrink-0 border-2 border-emerald-200/50 shadow-lg shadow-emerald-400/20">
                      <Icon name="Hash" size={12} className="text-green-100" />
                    </div>
                    <div className="min-w-0">
                      <div className="font-semibold text-sm sm:text-base text-emerald-100 mb-1">Webinar ID:</div>
                      <div className="text-green-100 text-xs sm:text-base font-mono break-all">{webinar.webinarId}</div>
                    </div>
                  </motion.div>
                </div>
              </motion.div>
            </div>
          </div>

          {/* Right Side: Money-Back Image */}
          <RefundPolicy navigate={navigate} />
        </div>
      </div>
    </div>
  );
}

export default WebinarHeader;